import React, { Component } from "react";
import {
    Button,
    Chip,
    CircularProgress,
    createStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    withStyles,
    WithStyles,
} from "@material-ui/core";
import { Star, InfoOutlined, WarningOutlined } from "@material-ui/icons";
import RHIApi, { RHIRound } from "../../api/rhi";
import { getSlidingScaleParams } from "./rhiUtils";

interface Props extends WithStyles {
    playerId: string;
    currentUserRule?: string;
    /** Render inside AuxiliaryDialog — title lives in dialog toolbar. */
    inDialog?: boolean;
}

interface State {
    loading: boolean;
    rhi: number | null;
    roundCount: number;
    rounds: RHIRound[];
    error: string | null;
    calculatingMissing: boolean;
    attemptedFetch: boolean;
    calculatedScores: number;
}

class RHIDisplay extends Component<Props, State> {
    state: State = {
        loading: true,
        rhi: null,
        roundCount: 0,
        rounds: [],
        error: null,
        calculatingMissing: false,
        attemptedFetch: false,
        calculatedScores: 0,
    };

    componentDidMount() {
        this.fetchRHI();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.playerId !== this.props.playerId) {
            this.setState({ attemptedFetch: false, calculatedScores: 0 }, () => {
                this.fetchRHI();
            });
        }
    }

    fetchRHI = async () => {
        const { playerId } = this.props;
        if (!playerId) return;
        this.setState({ loading: true, error: null, attemptedFetch: false, calculatedScores: 0 });
        try {
            const response = await RHIApi.getPlayerRHI(playerId);
            const { rhi, roundCount, rounds } = response.data.data;
            this.setState({ rhi, roundCount, rounds, loading: false });
        } catch (e) {
            this.setState({ error: "Failed to load RHI data", loading: false });
        }
    };

    handleCalculateMissing = async () => {
        const { playerId } = this.props;
        if (!playerId) return;
        this.setState({ calculatingMissing: true, error: null });
        try {
            const response = await RHIApi.calculateMissingDifferentials(playerId);
            const { rhi, roundCount, rounds, calculatedScores } = response.data.data;
            this.setState({ rhi, roundCount, rounds, calculatingMissing: false, attemptedFetch: true, calculatedScores });
        } catch (e) {
            this.setState({ error: "Failed to calculate missing differentials", calculatingMissing: false, attemptedFetch: true });
        }
    };

    /**
     * Determines which round indices (0-based, sorted by differential) are used in the RHI calculation.
     * Returns a Set of scoreIds that contribute to the current RHI.
     */
    getUsedRoundIds = (rounds: RHIRound[]): Set<string> => {
        const validRounds = rounds.filter(r => !r.courseRatingEstimated);
        const validCount = validRounds.length;
        const { useBest } = getSlidingScaleParams(validCount);
        const sorted = [...validRounds].sort((a, b) => (a.scoreDifferential ?? Infinity) - (b.scoreDifferential ?? Infinity));
        return new Set(sorted.slice(0, useBest).map(r => r.scoreId));
    };

    formatDate = (timestamp: number | null): string => {
        if (!timestamp) return "—";
        return new Date(timestamp).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    render() {
        const { classes, currentUserRule, inDialog = false } = this.props;
        const { loading, rhi, roundCount, rounds, error, calculatingMissing, attemptedFetch, calculatedScores } = this.state;
        const isAdmin = currentUserRule === "admin";
        const validRoundsCount = rounds.filter(r => !r.courseRatingEstimated).length;
        const shouldShowButton = isAdmin && validRoundsCount < 3 && (!attemptedFetch || calculatedScores > 0);
        const attemptedAndNoHistory = attemptedFetch && calculatedScores === 0 && validRoundsCount < 3;

        if (loading) {
            return (
                <div className={`${classes.centered}${inDialog ? ` ${classes.centeredDialog}` : ""}`}>
                    <CircularProgress size={24} />
                </div>
            );
        }

        if (error) {
            return (
                <Typography
                    color="error"
                    variant="body2"
                    className={`${classes.centered}${inDialog ? ` ${classes.centeredDialog}` : ""}`}
                >
                    {error}
                </Typography>
            );
        }

        const usedRoundIds = this.getUsedRoundIds(rounds);
        const hasRHI = rhi !== null;

        return (
            <Paper
                className={`${classes.container}${inDialog ? ` ${classes.containerDialog}` : ""}`}
                elevation={0}
            >
                {!inDialog ? (
                    <div className={classes.header}>
                        <Typography variant="h6" className={classes.title}>
                            Range Handicap Index
                        </Typography>
                        <Tooltip
                            title="Shows your playing handicap based on your recent rounds"
                            placement="top"
                        >
                            <InfoOutlined className={classes.infoIcon} />
                        </Tooltip>
                    </div>
                ) : (
                    <div className={classes.dialogInfoRow}>
                        <Tooltip
                            title="Shows your playing handicap based on your recent rounds"
                            placement="left"
                        >
                            <InfoOutlined className={classes.infoIcon} />
                        </Tooltip>
                    </div>
                )}

                <div className={`${classes.rhiValue}${inDialog ? ` ${classes.rhiValueDialog}` : ""}`}>
                    {hasRHI ? (
                        <>
                            <Typography variant="h3" color="primary" className={classes.rhiNumber}>
                                {rhi!.toFixed(1)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" style={{ fontSize: "0.9rem" }}>
                                From {validRoundsCount} valid round{validRoundsCount !== 1 ? "s" : ""}
                            </Typography>
                        </>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
                            <Typography variant="body1" color="textSecondary" style={{ fontSize: "0.9rem" }}>
                                {validRoundsCount < 3
                                    ? validRoundsCount === 0
                                        ? "At least 3 valid rounds needed"
                                        : `${3 - validRoundsCount} more valid round${3 - validRoundsCount !== 1 ? "s" : ""} needed`
                                    : "No RHI data"}
                            </Typography>
                            {attemptedAndNoHistory && (
                                <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.8rem" }}>
                                    No past rounds found on the system
                                </Typography>
                            )}
                            {shouldShowButton && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={this.handleCalculateMissing}
                                    disabled={calculatingMissing}
                                    startIcon={calculatingMissing ? <CircularProgress size={16} /> : null}
                                >
                                    {calculatingMissing ? "Calculating..." : "Recalculate missing RHI"}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {rounds.length > 0 && (
                    <>
                        <Typography variant="subtitle2" className={classes.tableTitle}>
                            Recent rounds
                            <Tooltip title="Used rounds are highlighted" placement="right">
                                <InfoOutlined className={classes.smallInfo} />
                            </Tooltip>
                        </Typography>
                        <div className={`${classes.tableWrapper}${inDialog ? ` ${classes.tableWrapperDialog}` : ""}`}>
                            <Table size="small" className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="none"></TableCell>
                                        <TableCell align="left">Score</TableCell>
                                        <TableCell align="left">Course</TableCell>
                                        <TableCell align="left">Date</TableCell>
                                        <TableCell align="left">Code</TableCell>
                                        <TableCell align="left">Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rounds.map(round => {
                                        const isUsed = usedRoundIds.has(round.scoreId);
                                        return (
                                            <TableRow
                                                key={round.scoreId}
                                                className={isUsed ? classes.usedRow : ""}
                                            >
                                                <TableCell padding="none" align="center" style={{ width: 40 }}>
                                                    {isUsed ? (
                                                        <Star className={classes.starIcon} fontSize="small" />
                                                    ) : round.courseRatingEstimated ? (
                                                        <Tooltip title={round.missingCourseData?.length ? `Invalid: ${round.missingCourseData.join(", ")}` : "Invalid: Course data unavailable"} placement="right">
                                                            <WarningOutlined className={classes.estimated} style={{ marginTop: 4 }} fontSize="small" />
                                                        </Tooltip>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Chip
                                                        size="small"
                                                        label={round.scoreDifferential != null ? round.scoreDifferential.toFixed(1) : "—"}
                                                        className={isUsed ? classes.usedChip : classes.unusedChip}
                                                    />
                                                </TableCell>
                                                <TableCell align="left">
                                                    {round.courseName ?? "Unknown"}
                                                </TableCell>
                                                <TableCell align="left" style={{ whiteSpace: "nowrap" }}>{this.formatDate(round.gameDate)}</TableCell>
                                                <TableCell align="left">{round.gameCode ?? "—"}</TableCell>
                                                <TableCell align="left">
                                                    {round.courseRatingEstimated ? (
                                                        <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
                                                            {round.missingCourseData?.join(", ") || "Incomplete data"}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary" style={{ opacity: 0.5 }}>
                                                            Valid round
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                        <div className={classes.legendContainer}>
                            <div className={classes.legendItem}>
                                <Star className={classes.starIcon} style={{ fontSize: 16 }} />
                                <Typography variant="caption" color="textSecondary" className={classes.legendText}>
                                    Round contributes to your Handicap Index
                                </Typography>
                            </div>
                            {rounds.some(r => r.courseRatingEstimated) && (
                                <div className={classes.legendItem}>
                                    <WarningOutlined className={classes.estimated} style={{ fontSize: 16 }} />
                                    <Typography variant="caption" color="textSecondary" className={classes.legendText}>
                                        Round invalid due to incomplete/missing tracking data
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Paper>
        );
    }
}

const styles = (theme: Theme) =>
    createStyles({
        container: {
            padding: theme.spacing(2),
            marginTop: theme.spacing(1),
        },
        containerDialog: {
            marginTop: 0,
            padding: theme.spacing(2.5, 2.5, 2),
            backgroundColor: "transparent",
            [theme.breakpoints.up(501)]: {
                padding: theme.spacing(3, 3.5, 2.5),
            },
        },
        header: {
            display: "flex",
            alignItems: "center",
            marginBottom: theme.spacing(1),
        },
        dialogInfoRow: {
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: theme.spacing(-0.5),
        },
        title: {
            fontWeight: "bold",
            flexGrow: 1,
            fontSize: "1.1rem",
            [theme.breakpoints.down("sm")]: {
                fontSize: "1rem",
            },
        },
        infoIcon: {
            fontSize: 18,
            color: theme.palette.text.secondary,
            cursor: "help",
            [theme.breakpoints.down("sm")]: {
                fontSize: 16,
            },
        },
        smallInfo: {
            fontSize: 14,
            color: theme.palette.text.secondary,
            marginLeft: 4,
            cursor: "help",
            verticalAlign: "middle",
            [theme.breakpoints.down("sm")]: {
                fontSize: 12,
            },
        },
        rhiValue: {
            textAlign: "center",
            padding: theme.spacing(1, 0, 2),
        },
        rhiValueDialog: {
            paddingTop: theme.spacing(0.5),
            [theme.breakpoints.up(501)]: {
                paddingTop: theme.spacing(1),
            },
        },
        rhiNumber: {
            fontWeight: "bold",
            fontSize: "3rem",
            [theme.breakpoints.down("sm")]: {
                fontSize: "2.5rem",
            },
        },
        tableTitle: {
            marginBottom: theme.spacing(0.5),
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
        },
        tableWrapper: {
            overflowX: "auto",
            [theme.breakpoints.down("sm")]: {
                maxWidth: "100vw",
                marginLeft: theme.spacing(-2),
                marginRight: theme.spacing(-2),
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
        },
        tableWrapperDialog: {
            [theme.breakpoints.up(501)]: {
                marginLeft: 0,
                marginRight: 0,
                paddingLeft: 0,
                paddingRight: 0,
                maxWidth: "none",
            },
        },
        table: {
            marginTop: theme.spacing(0.5),
            minWidth: 650,
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.85rem",
            },
        },
        usedRow: {
            backgroundColor: "rgba(103, 58, 183, 0.06)",
        },
        starIcon: {
            color: theme.palette.secondary.main,
            marginTop: 2,
            [theme.breakpoints.down("sm")]: {
                fontSize: 16,
            },
        },
        usedChip: {
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: "bold",
            fontSize: "0.8rem",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.75rem",
            },
        },
        unusedChip: {
            backgroundColor: theme.palette.grey[200],
            fontSize: "0.8rem",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.75rem",
            },
        },
        estimated: {
            color: theme.palette.text.hint,
            fontSize: 16,
        },
        legendContainer: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
            marginTop: theme.spacing(2),
            padding: theme.spacing(1.5),
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.grey[200]}`,
        },
        legendItem: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
        },
        legendText: {
            fontSize: "0.75rem",
            lineHeight: 1.4,
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.7rem",
            },
        },
        centered: {
            display: "flex",
            justifyContent: "center",
            padding: theme.spacing(2),
        },
        centeredDialog: {
            padding: theme.spacing(4, 3.5),
        },
    });

export default withStyles(styles)(RHIDisplay);
